import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, query, where, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { CheckCircle, XCircle, Hourglass, Search, Star, User, Calendar, MessageSquare, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Resena {
  id: string;
  perfil_id: string;
  tipo_perfil: 'asesor' | 'asociado';
  nombre_usuario: string;
  reseña: string;
  razon: string;
  calificacionFinal: number;
  estrellas: number;
  fecha: { seconds: number };
  estatus: 'pendiente' | 'aprobada' | 'rechazada';
  calificacionPerfilAlMomento?: number;
}

interface Perfil {
  id: string;
  docId: string;
  nombre: string;
  tipo: 'asesor' | 'asociado';
}

export default function AdminAprobarResenas() {
  const { user } = useUser();
  const navigate = useNavigate();

  const [allReviews, setAllReviews] = useState<Resena[]>([]);
  const [tabActivo, setTabActivo] = useState<'pendiente' | 'aprobada' | 'rechazada'>('pendiente');
  const [loading, setLoading] = useState(true);
  const [perfiles, setPerfiles] = useState<Perfil[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [procesando, setProcesando] = useState<string | null>(null);

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const resenasPorPagina = 10;

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    pendientes: 0,
    aprobadas: 0,
    rechazadas: 0,
    promedioCalificacion: 0
  });

  const cargarPerfiles = async () => {
    try {
      // ✅ Cargar asesores usando doc.id
      const asesoresSnap = await getDocs(collection(db, 'asesor'));
      const asesores: Perfil[] = asesoresSnap.docs.map(docSnap => ({
        id: docSnap.id, // Usar doc.id como principal
        docId: docSnap.id,
        nombre: docSnap.data().nombre || 'Sin nombre',
        tipo: 'asesor'
      }));

      // ✅ Cargar asociados usando doc.id
      const asociadosSnap = await getDocs(collection(db, 'asociado'));
      const asociados: Perfil[] = asociadosSnap.docs.map(docSnap => ({
        id: docSnap.id, // Usar doc.id como principal
        docId: docSnap.id,
        nombre: docSnap.data().nombre || 'Sin nombre',
        tipo: 'asociado'
      }));

      setPerfiles([...asesores, ...asociados]);
      console.log(`✅ Perfiles cargados: ${asesores.length} asesores, ${asociados.length} asociados`);
    } catch (error) {
      console.error('❌ Error cargando perfiles:', error);
    }
  };

  useEffect(() => {
    const cargarDatos = async () => {
      // Validar acceso de administrador
      if (!user || !user.tipo?.includes('admin')) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para acceder a esta sección.'
        });
        navigate('/admin-dashboard');
        return;
      }

      try {
        setLoading(true);

        // Cargar reseñas
        const snap = await getDocs(collection(db, 'calificacion'));
        const reviews: Resena[] = snap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        } as Resena));

        setAllReviews(reviews);

        // Calcular estadísticas
        const pendientes = reviews.filter(r => r.estatus === 'pendiente').length;
        const aprobadas = reviews.filter(r => r.estatus === 'aprobada').length;
        const rechazadas = reviews.filter(r => r.estatus === 'rechazada').length;
        const promedioCalificacion = reviews.length > 0
          ? Math.round(reviews.reduce((acc, r) => acc + (r.calificacionFinal || 0), 0) / reviews.length)
          : 0;

        setStats({
          total: reviews.length,
          pendientes,
          aprobadas,
          rechazadas,
          promedioCalificacion
        });

        // Cargar perfiles
        await cargarPerfiles();

        console.log(`✅ Reseñas cargadas: ${reviews.length} total`);
      } catch (error) {
        console.error('❌ Error cargando reseñas:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al cargar las reseñas.'
        });
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [user, navigate]);

  const actualizarEstatus = async (id: string, nuevo: 'aprobada' | 'rechazada') => {
    setProcesando(id);

    try {
      const ref = doc(db, 'calificacion', id);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        throw new Error('Reseña no encontrada.');
      }

      const resena = snap.data() as Resena;
      const { perfil_id, tipo_perfil } = resena;

      // ✅ Buscar el documento real usando doc.id (primero intentamos con el perfil_id directamente)
      let perfilDocId = perfil_id;
      let perfilDoc;

      try {
        // Intentar buscar directamente con el ID
        const perfilRef = doc(db, tipo_perfil, perfil_id);
        const perfilSnap = await getDoc(perfilRef);

        if (perfilSnap.exists()) {
          perfilDoc = perfilSnap;
          perfilDocId = perfilSnap.id;
        } else {
          // Si no existe, buscar por campo personalizado (legacy)
          const perfilQuery = query(
            collection(db, tipo_perfil),
            where(`${tipo_perfil}_id`, '==', perfil_id)
          );
          const perfilQuerySnap = await getDocs(perfilQuery);

          if (perfilQuerySnap.empty) {
            throw new Error(`No se encontró el ${tipo_perfil} con ID ${perfil_id}`);
          }

          perfilDoc = perfilQuerySnap.docs[0];
          perfilDocId = perfilDoc.id;
        }
      } catch (error) {
        console.error(`❌ Error buscando perfil:`, error);
        throw new Error(`No se pudo encontrar el perfil del ${tipo_perfil}`);
      }

      // ✅ Solo calcular promedio si se aprueba la reseña
      if (nuevo === 'aprobada') {
        // Obtener todas las reseñas aprobadas (incluyendo la actual)
        const q = query(
          collection(db, 'calificacion'),
          where('perfil_id', '==', perfil_id),
          where('estatus', '==', 'aprobada')
        );
        const calSnap = await getDocs(q);

        // Incluir la reseña actual en el cálculo
        const todasCalificaciones = [
          ...calSnap.docs.map(d => d.data() as Resena),
          { ...resena, estatus: 'aprobada' } // Incluir la actual
        ];

        const totalCalificaciones = todasCalificaciones.filter(r => r.id !== id).length + 1;
        const sumaCalificaciones = todasCalificaciones.reduce((acc, curr) => acc + (curr.calificacionFinal || 0), 0);
        const promedio = Math.round(sumaCalificaciones / totalCalificaciones);

        // Actualizar el perfil con el nuevo promedio
        await updateDoc(doc(db, tipo_perfil, perfilDocId), {
          calificacion: promedio
        });

        // Actualizar la reseña con el promedio del momento
        await updateDoc(ref, {
          calificacionPerfilAlMomento: promedio,
          estatus: nuevo
        });

        console.log(`✅ Promedio actualizado para ${tipo_perfil}: ${promedio}`);
      } else {
        // Si se rechaza, solo actualizar el estatus
        await updateDoc(ref, {
          estatus: nuevo
        });
      }

      // Actualizar estado local
      setAllReviews(prev =>
        prev.map(r => (r.id === id ? { ...r, estatus: nuevo } : r))
      );

      // Actualizar estadísticas
      setStats(prev => ({
        ...prev,
        pendientes: prev.pendientes - (resena.estatus === 'pendiente' ? 1 : 0),
        aprobadas: prev.aprobadas + (nuevo === 'aprobada' ? 1 : 0),
        rechazadas: prev.rechazadas + (nuevo === 'rechazada' ? 1 : 0)
      }));

      Swal.fire({
        title: '¡Éxito!',
        text: `La reseña fue ${nuevo === 'aprobada' ? 'aprobada y publicada' : 'rechazada'} correctamente.`,
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2000
      });

    } catch (error: any) {
      console.error('❌ Error al actualizar reseña:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Hubo un problema al actualizar la reseña.',
        icon: 'error',
      });
    } finally {
      setProcesando(null);
    }
  };

  const eliminarResena = async (id: string) => {
    const resena = allReviews.find(r => r.id === id);
    if (!resena) return;

    // Confirmar eliminación con SweetAlert2
    const result = await Swal.fire({
      title: '¿Eliminar reseña?',
      html: `
        <p>¿Estás seguro de que deseas eliminar esta reseña?</p>
        <div class="text-start mt-3">
          <strong>Usuario:</strong> ${resena.nombre_usuario}<br>
          <strong>Comentario:</strong> ${resena.reseña.substring(0, 100)}${resena.reseña.length > 100 ? '...' : ''}<br>
          <strong>Estado:</strong> <span class="badge bg-${resena.estatus === 'pendiente' ? 'warning' : resena.estatus === 'aprobada' ? 'success' : 'danger'}">${resena.estatus}</span>
        </div>
        <p class="text-danger mt-3 mb-0"><strong>⚠️ Esta acción no se puede deshacer.</strong></p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setProcesando(id);

    try {
      // Si la reseña está aprobada, recalcular el promedio del perfil
      if (resena.estatus === 'aprobada') {
        const { perfil_id, tipo_perfil } = resena;

        // Buscar el documento del perfil
        let perfilDocId = perfil_id;
        try {
          const perfilRef = doc(db, tipo_perfil, perfil_id);
          const perfilSnap = await getDoc(perfilRef);

          if (perfilSnap.exists()) {
            perfilDocId = perfilSnap.id;
          } else {
            const perfilQuery = query(
              collection(db, tipo_perfil),
              where(`${tipo_perfil}_id`, '==', perfil_id)
            );
            const perfilQuerySnap = await getDocs(perfilQuery);
            if (!perfilQuerySnap.empty) {
              perfilDocId = perfilQuerySnap.docs[0].id;
            }
          }
        } catch (error) {
          console.warn('⚠️ No se pudo encontrar el perfil para recalcular promedio:', error);
        }

        // Obtener todas las reseñas aprobadas excepto la que se va a eliminar
        const q = query(
          collection(db, 'calificacion'),
          where('perfil_id', '==', perfil_id),
          where('estatus', '==', 'aprobada')
        );
        const calSnap = await getDocs(q);
        const resenasAprobadas = calSnap.docs
          .filter(d => d.id !== id)
          .map(d => d.data() as Resena);

        // Calcular nuevo promedio
        if (resenasAprobadas.length > 0) {
          const sumaCalificaciones = resenasAprobadas.reduce((acc, curr) => acc + (curr.calificacionFinal || 0), 0);
          const promedio = Math.round(sumaCalificaciones / resenasAprobadas.length);

          // Actualizar el perfil con el nuevo promedio
          await updateDoc(doc(db, tipo_perfil, perfilDocId), {
            calificacion: promedio
          });

          console.log(`✅ Promedio recalculado después de eliminar: ${promedio}`);
        } else {
          // Si no quedan reseñas aprobadas, establecer calificación en 0
          await updateDoc(doc(db, tipo_perfil, perfilDocId), {
            calificacion: 0
          });

          console.log('✅ No quedan reseñas aprobadas, calificación establecida en 0');
        }
      }

      // Eliminar la reseña de Firestore
      await deleteDoc(doc(db, 'calificacion', id));

      // Actualizar estado local
      const nuevasResenas = allReviews.filter(r => r.id !== id);
      setAllReviews(nuevasResenas);

      // Recalcular estadísticas
      const pendientes = nuevasResenas.filter(r => r.estatus === 'pendiente').length;
      const aprobadas = nuevasResenas.filter(r => r.estatus === 'aprobada').length;
      const rechazadas = nuevasResenas.filter(r => r.estatus === 'rechazada').length;
      const promedioCalificacion = nuevasResenas.length > 0
        ? Math.round(nuevasResenas.reduce((acc, r) => acc + (r.calificacionFinal || 0), 0) / nuevasResenas.length)
        : 0;

      setStats({
        total: nuevasResenas.length,
        pendientes,
        aprobadas,
        rechazadas,
        promedioCalificacion
      });

      Swal.fire({
        title: '¡Eliminada!',
        text: 'La reseña ha sido eliminada correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2000
      });

    } catch (error: any) {
      console.error('❌ Error al eliminar reseña:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Hubo un problema al eliminar la reseña.',
        icon: 'error',
      });
    } finally {
      setProcesando(null);
    }
  };

  // Reiniciar página actual cuando cambia el tab o la búsqueda
  useEffect(() => {
    setPaginaActual(1);
  }, [tabActivo, busqueda]);

  // Filtrar reseñas por tab y búsqueda
  const resenasFiltradas = allReviews
    .filter(r => r.estatus === tabActivo)
    .filter(r => {
      if (!busqueda.trim()) return true;
      const searchLower = busqueda.toLowerCase();
      const perfil = perfiles.find(p => p.id === r.perfil_id);
      return (
        r.nombre_usuario.toLowerCase().includes(searchLower) ||
        r.reseña.toLowerCase().includes(searchLower) ||
        perfil?.nombre.toLowerCase().includes(searchLower)
      );
    });

  // Paginación
  const totalPaginas = Math.ceil(resenasFiltradas.length / resenasPorPagina);
  const indiceInicio = (paginaActual - 1) * resenasPorPagina;
  const indiceFin = indiceInicio + resenasPorPagina;
  const resenasPaginadas = resenasFiltradas.slice(indiceInicio, indiceFin);

  const colores = {
    pendiente: { bg: 'warning', border: 'warning', text: 'warning' },
    aprobada: { bg: 'success', border: 'success', text: 'success' },
    rechazada: { bg: 'danger', border: 'danger', text: 'danger' },
  };

  const iconos = {
    pendiente: <Hourglass size={18} />,
    aprobada: <CheckCircle size={18} />,
    rechazada: <XCircle size={18} />,
  };

  const renderEstrellas = (estrellas: number) => {
    return (
      <div className="d-inline-flex align-items-center">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < estrellas ? 'text-warning' : 'text-muted'}
            fill={i < estrellas ? 'currentColor' : 'none'}
          />
        ))}
        <span className="ms-2 text-muted">({estrellas}/5)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <UserNav />
        <div className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando reseñas...</p>
        </div>
      </>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <>
        <UserNav />
        <div className="container mt-5">
          <div className="alert alert-danger">
            <h4>Acceso Denegado</h4>
            <p>No tienes permiso para acceder a esta sección</p>
            <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
              Volver al Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Aprobación de Reseñas</h2>
          <span className="ipn-subtitle">Gestión y moderación de reseñas</span>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Aprobación de Reseñas' }
            ]}
          />

          {/* Tarjetas de Estadísticas */}
          <div className="row g-3 mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-primary bg-opacity-10 p-3 rounded">
                        <MessageSquare className="text-primary" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Total Reseñas</h6>
                      <h3 className="mb-0">{stats.total}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-warning bg-opacity-10 p-3 rounded">
                        <Hourglass className="text-warning" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Pendientes</h6>
                      <h3 className="mb-0">{stats.pendientes}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 p-3 rounded">
                        <CheckCircle className="text-success" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Aprobadas</h6>
                      <h3 className="mb-0">{stats.aprobadas}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-danger bg-opacity-10 p-3 rounded">
                        <XCircle className="text-danger" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Rechazadas</h6>
                      <h3 className="mb-0">{stats.rechazadas}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros y Búsqueda */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-6">
                  <div className="btn-group w-100" role="group">
                    {(['pendiente', 'aprobada', 'rechazada'] as const).map((estado) => (
                      <button
                        key={estado}
                        className={`btn ${tabActivo === estado ? `btn-${colores[estado].bg}` : `btn-outline-${colores[estado].border}`}`}
                        onClick={() => setTabActivo(estado)}
                      >
                        <span className="d-inline-flex align-items-center gap-2">
                          {iconos[estado]}
                          <span>{estado.charAt(0).toUpperCase() + estado.slice(1)}</span>
                          <span className="badge bg-white text-dark">{
                            estado === 'pendiente' ? stats.pendientes :
                            estado === 'aprobada' ? stats.aprobadas :
                            stats.rechazadas
                          }</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <Search size={18} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por usuario, comentario o perfil..."
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Reseñas */}
          {resenasFiltradas.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <MessageSquare size={48} className="text-muted mb-3" />
                <h5 className="text-muted">No hay reseñas {tabActivo}s</h5>
                <p className="text-muted mb-0">
                  {busqueda ? 'Intenta con otros términos de búsqueda' : 'Las reseñas aparecerán aquí'}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="row g-3">
                {resenasPaginadas.map((res) => {
                const perfil = perfiles.find(p => p.id === res.perfil_id);
                return (
                  <div key={res.id} className="col-12">
                    <div className={`card border-start border-${colores[tabActivo].border} border-4 shadow-sm h-100`}>
                      <div className="card-body">
                        <div className="row">
                          {/* Columna Principal */}
                          <div className="col-lg-8">
                            <div className="d-flex align-items-start mb-3">
                              <div className={`badge bg-${colores[tabActivo].bg} me-2`}>
                                {tabActivo.toUpperCase()}
                              </div>
                              <div className="flex-grow-1">
                                <h5 className="mb-1">
                                  <User size={18} className="me-2" />
                                  {perfil?.nombre || 'Perfil no encontrado'}
                                </h5>
                                <span className="badge bg-secondary">
                                  {res.tipo_perfil === 'asesor' ? 'Asesor' : 'Asociado'}
                                </span>
                              </div>
                            </div>

                            <div className="mb-3">
                              <strong className="text-muted d-block mb-1">Opinión de:</strong>
                              <p className="mb-0">{res.nombre_usuario}</p>
                            </div>

                            <div className="mb-3">
                              <strong className="text-muted d-block mb-1">Comentario:</strong>
                              <p className="mb-0 text-break">{res.reseña}</p>
                            </div>

                            {res.razon && (
                              <div className="mb-3">
                                <strong className="text-muted d-block mb-1">Razón de contacto:</strong>
                                <p className="mb-0">{res.razon}</p>
                              </div>
                            )}
                          </div>

                          {/* Columna de Calificación y Acciones */}
                          <div className="col-lg-4">
                            <div className="border-start ps-3 h-100 d-flex flex-column">
                              <div className="mb-3">
                                <strong className="text-muted d-block mb-2">Calificación:</strong>
                                {renderEstrellas(res.estrellas)}
                                <div className="mt-2">
                                  <span className="badge bg-primary">
                                    {res.calificacionFinal}/100 puntos
                                  </span>
                                </div>
                              </div>

                              <div className="mb-3">
                                <strong className="text-muted d-block mb-1">
                                  <Calendar size={14} className="me-1" />
                                  Fecha:
                                </strong>
                                <small>
                                  {new Date(res.fecha.seconds * 1000).toLocaleDateString('es-MX', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </small>
                              </div>

                              {res.calificacionPerfilAlMomento && (
                                <div className="mb-3">
                                  <strong className="text-muted d-block mb-1">Promedio al aprobar:</strong>
                                  <span className="badge bg-info">
                                    {res.calificacionPerfilAlMomento}/100
                                  </span>
                                </div>
                              )}

                              {/* Botones de Acción */}
                              <div className="mt-auto">
                                <div className="d-grid gap-2">
                                  {tabActivo === 'pendiente' && (
                                    <>
                                      <button
                                        className="btn btn-success"
                                        onClick={() => actualizarEstatus(res.id, 'aprobada')}
                                        disabled={procesando === res.id}
                                      >
                                        {procesando === res.id ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Procesando...
                                          </>
                                        ) : (
                                          <>
                                            <CheckCircle size={16} className="me-2" />
                                            Aprobar
                                          </>
                                        )}
                                      </button>
                                      <button
                                        className="btn btn-warning"
                                        onClick={() => actualizarEstatus(res.id, 'rechazada')}
                                        disabled={procesando === res.id}
                                      >
                                        <XCircle size={16} className="me-2" />
                                        Rechazar
                                      </button>
                                    </>
                                  )}
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => eliminarResena(res.id)}
                                    disabled={procesando === res.id}
                                  >
                                    <Trash2 size={16} className="me-2" />
                                    Eliminar
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Controles de Paginación */}
            {totalPaginas > 1 && (
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-body">
                  <nav>
                    <ul className="pagination justify-content-center mb-0">
                      <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
                          disabled={paginaActual === 1}
                        >
                          Anterior
                        </button>
                      </li>

                      {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(numeroPagina => {
                        // Mostrar solo algunas páginas alrededor de la actual
                        if (
                          numeroPagina === 1 ||
                          numeroPagina === totalPaginas ||
                          (numeroPagina >= paginaActual - 2 && numeroPagina <= paginaActual + 2)
                        ) {
                          return (
                            <li
                              key={numeroPagina}
                              className={`page-item ${paginaActual === numeroPagina ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setPaginaActual(numeroPagina)}
                              >
                                {numeroPagina}
                              </button>
                            </li>
                          );
                        } else if (
                          numeroPagina === paginaActual - 3 ||
                          numeroPagina === paginaActual + 3
                        ) {
                          return (
                            <li key={numeroPagina} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}

                      <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
                          disabled={paginaActual === totalPaginas}
                        >
                          Siguiente
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}

            {/* Información adicional */}
            {resenasFiltradas.length > 0 && (
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Mostrando {indiceInicio + 1} - {Math.min(indiceFin, resenasFiltradas.length)} de {resenasFiltradas.length} reseñas filtradas ({stats.total} totales)
                    </small>
                    <small className="text-muted">
                      Promedio general: <strong>{stats.promedioCalificacion}/100</strong>
                    </small>
                  </div>
                </div>
              </div>
            )}
          </>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
